import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import { Modal, Spinner } from 'react-bootstrap';
import api from '../../services/api';

import ViewCILModal from './ViewCILModal';   // View modal
import EditCILModal from './EditCILModal';   // Edit modal

const CILsTable = () => {
  // Table data
  const [cils, setCILs] = useState([]);

  // Search filter (text) and status filter
  const [filterText, setFilterText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Modals for view, edit, rejection, and various approvals
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApprovePendingModal, setShowApprovePendingModal] = useState(false);
  const [showApproveContractPendingModal, setShowApproveContractPendingModal] = useState(false);
  const [showApproveContractApproveModal, setShowApproveContractApproveModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

  // Current / selected CIL
  const [currentCIL, setCurrentCIL] = useState(null);
  const [editedCIL, setEditedCIL] = useState(null);
  const [selectedCIL, setSelectedCIL] = useState(null);
  const [deletedCIL, setDeletedCIL] = useState(null);

  // Input fields for modals
  const [expirationDate, setExpirationDate] = useState('');
  const [actualAmount, setActualAmount] = useState('');
  const [dealValue, setDealValue] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  // Retrieve user type from localStorage
  const storedUser = localStorage.getItem('user');
  const userType = storedUser ? JSON.parse(storedUser).type : 'developerSales'; // default if not set

  // Define approved statuses
  const approvedStatuses = [
    'approvedByDeveloper',
    'contractPendingApproval',
    'contractApproved',
    'approved',
    'paid',
  ];

  // Helper: map raw status to a user-friendly string
  const readableStatus = (status) => {
    if (userType === 'developerSales') {
      if (status === 'pending') return 'Pending';
      if (status === 'rejected') return 'Rejected';
      if (status === 'expired') return 'Expired';
      if (approvedStatuses.includes(status)) return 'Approved';
      return status;
    } else {
      // For admin: convert somethingLikeThis => Something Like This
      return status
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase());
    }
  };

  // Helper: map raw status to a CSS class
  const statusClass = (status) => {
    if (userType === 'developerSales') {
      if (status === 'pending') return 'warning';
      if (status === 'rejected') return 'danger';
      if (status === 'expired') return 'danger';
      if (approvedStatuses.includes(status)) return 'success';
      return 'warning';
    } else {
      if (status === 'approved' || status === 'paid') return 'success';
      if (status === 'expired' || status === 'rejected') return 'danger';
      return 'warning';
    }
  };

  // Fetch CILs on mount
  const fetchCILs = async () => {
    try {
      const res = await api.get('/cil/unpaginated?isCustom=0');
      if (res.data.response) {
        setCILs(res.data.response);
      }
    } catch (error) {
      console.error('Error fetching CILs:', error);
      toast.error('Failed to load CILs');
    }
  };

  useEffect(() => {
    fetchCILs();
  }, []);

  // --------------------- View and Edit Handlers ---------------------

  // View
  const handleView = (cil) => {
    setCurrentCIL(cil);
    setShowViewModal(true);
  };
  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setCurrentCIL(null);
    // Re-fetch in case data changed
    fetchCILs();
  };

  // Edit
  const handleEdit = (cil) => {
    setEditedCIL(cil);
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditedCIL(null);
    // Re-fetch in case data changed
    fetchCILs();
  };
  const handleSaveEdit = (updatedCIL) => {
    // Optionally update local data without full refetch:
    const updated = cils.map((c) => (c.id === updatedCIL.id ? updatedCIL : c));
    setCILs(updated);
    setShowEditModal(false);
    setEditedCIL(null);
    fetchCILs();
  };

  // --------------------- Approval Handlers ---------------------

  // Approve Pending (status: 'pending')
  const handleApprovePending = (cil) => {
    setSelectedCIL(cil);
    setShowApprovePendingModal(true);
  };
  const handleCloseApprovePendingModal = () => {
    setShowApprovePendingModal(false);
    setExpirationDate('');
    setSelectedCIL(null);
    // Re-fetch
    fetchCILs();
  };
  const handleApprovePendingSubmit = async () => {
    setLoading(true);
    try {
      await api.post(`/cil/${selectedCIL.id}/approveDeveloper`, {
        expirationDate,
      });
      toast.success('CIL approved successfully!');
      handleCloseApprovePendingModal();
    } catch (error) {
      console.error(error);
      toast.error('Error approving CIL.');
    }
    setLoading(false);
  };

  // Approve Contract Pending (status: 'contractPendingApproval')
  const handleApproveContractPending = (cil) => {
    setSelectedCIL(cil);
    setShowApproveContractPendingModal(true);
  };
  const handleCloseApproveContractPendingModal = () => {
    setShowApproveContractPendingModal(false);
    setActualAmount('');
    setDealValue('');
    setSelectedCIL(null);
    fetchCILs();
  };
  const handleApproveContractPendingSubmit = async () => {
    setLoading(true);
    try {
      await api.post(`/cil/${selectedCIL.id}/approveContract`, {
        actualAmount,
        dealValue,
      });
      toast.success('Contract pending CIL approved successfully!');
      handleCloseApproveContractPendingModal();
    } catch (error) {
      console.error(error);
      toast.error('Error approving contract pending CIL.');
    }
    setLoading(false);
  };

  // Approve Contract Approved (status: 'contractApproved')
  const handleApproveContractApprove = (cil) => {
    setSelectedCIL(cil);
    setShowApproveContractApproveModal(true);
  };
  const handleCloseApproveContractApproveModal = () => {
    setShowApproveContractApproveModal(false);
    setSelectedCIL(null);
    fetchCILs();
  };
  const handleApproveContractApproveSubmit = async () => {
    setLoading(true);
    try {
      await api.post(`/cil/${selectedCIL.id}/approveFinal`);
      toast.success('Final approval successful!');
      handleCloseApproveContractApproveModal();
    } catch (error) {
      console.error(error);
      toast.error('Error finalizing approval.');
    }
    setLoading(false);
  };

  // --------------------- Rejection Handler ---------------------
  const openRejectModal = (cil) => {
    setSelectedCIL(cil);
    setShowRejectModal(true);
  };
  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
    setRejectReason('');
    setSelectedCIL(null);
    fetchCILs();
  };
  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection.');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/cil/${selectedCIL.id}/reject`, { reason: rejectReason });
      toast.success('CIL rejected successfully!');
      handleCloseRejectModal();
    } catch (error) {
      console.error(error);
      toast.error('Error rejecting CIL.');
    }
    setLoading(false);
  };

  // --------------------- Define Columns for DataTable ---------------------
  const columns = [
    {
      name: 'No.',
      selector: (row) => row.id,
      sortable: true,
      width: '70px',
    },
    {
      name: 'Developer Name',
      selector: (row) => row.developerName || 'N/A',
      sortable: true,
    },
    {
      name: 'Broker Name',
      selector: (row) => row.broker?.name || 'N/A',
      sortable: true,
    },
    {
      name: 'Broker Number',
      selector: (row) => row.broker?.mobile || 'N/A',
      sortable: true,
    },
    {
      name: 'Client Name',
      selector: (row) => row.clientName || 'N/A',
      sortable: true,
    },
    {
      name: 'Client Number',
      selector: (row) => row.clientNumber || 'N/A',
      sortable: true,
    },
    {
      name: 'Project',
      selector: (row) => row.project?.name || 'N/A',
      sortable: true,
    },
    {
      name: 'Status',
      cell: (row) => (
        <span
          style={{ whiteSpace: 'nowrap' }}
          className={`bg-${statusClass(row.status)}-focus text-${statusClass(row.status)
            }-main px-24 py-4 rounded-pill fw-medium text-sm`}
        >
          {readableStatus(row.status)}
        </span>
      ),
      sortable: true,
      minWidth: '180px',
    },
    {
      name: 'Actions',
      cell: (row) => {
        const actions = [];

        if (userType === 'admin') {
          if (row.status === 'pending') {
            actions.push(
              <button
                key="approvePending"
                className="btn btn-success btn-sm me-2"
                onClick={() => handleApprovePending(row)}
              >
                Approve
              </button>
            );
          }
          if (row.status === 'approvedByDeveloper' && row.document) {
            actions.push(
              <button
                key="approveContractPending"
                className="btn btn-success btn-sm me-2"
                onClick={() => handleApproveContractPending(row)}
              >
                Approve Pending Contract
              </button>
            );
          }
          if (row.status === 'contractPendingApproval') {
            actions.push(
              <button
                key="approveContractPending"
                className="btn btn-success btn-sm me-2"
                onClick={() => handleApproveContractPending(row)}
              >
                Approve Pending Contract
              </button>
            );
          }
          if (row.status === 'contractApproved') {
            actions.push(
              <button
                key="finalApprove"
                className="btn btn-success btn-sm me-2"
                onClick={() => handleApproveContractApprove(row)}
              >
                Final Approve
              </button>
            );
          }
          if (!['expired', 'paid', 'rejected', 'approved'].includes(row.status)) {
            actions.push(
              <button
                key="reject"
                className="btn btn-danger btn-sm me-2"
                onClick={() => openRejectModal(row)}
              >
                Reject
              </button>
            );
          }
          actions.push(
            <button
              key="view"
              className="btn btn-primary btn-sm"
              onClick={() => handleView(row)}
            >
              View
            </button>
          );
        } else if (userType === 'developerSales') {
          if (row.status === 'pending') {
            actions.push(
              <button
                key="approvePending"
                className="btn btn-success btn-sm me-2"
                onClick={() => handleApprovePending(row)}
              >
                Approve
              </button>
            );
            actions.push(
              <button
                key="reject"
                className="btn btn-danger btn-sm me-2"
                onClick={() => openRejectModal(row)}
              >
                Reject
              </button>
            );
          }
          actions.push(
            <button
              key="view"
              className="btn btn-primary btn-sm"
              onClick={() => handleView(row)}
            >
              View
            </button>
          );
        } else {
          // If some other userType, just a View button
          actions.push(
            <button
              key="view"
              className="btn btn-primary btn-sm"
              onClick={() => handleView(row)}
            >
              View
            </button>
          );
        }
        return <div className="d-flex flex-wrap align-items-center">{actions}</div>;
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: '460px',
      maxWidth: '500px',
    },
  ];

  // --------------------- Filtering ---------------------
  // We'll create a set of distinct statuses to populate a dropdown filter
  const distinctStatuses = Array.from(
    new Set(cils.map((cil) => cil.status))
  ).sort(); // e.g. ['pending', 'rejected', 'expired', ...]

  // Combined filter:
  // 1) Matches filterText across multiple fields
  // 2) Matches selectedStatus if chosen
  const filteredData = cils.filter((cil) => {
    const search = filterText.toLowerCase();
    const matchesText =
      String(cil.id).toLowerCase().includes(search) ||
      (cil.developerName || '').toLowerCase().includes(search) ||
      (cil.broker?.name || '').toLowerCase().includes(search) ||
      (cil.broker?.mobile || '').toLowerCase().includes(search) ||
      (cil.clientName || '').toLowerCase().includes(search) ||
      (cil.clientNumber || '').toLowerCase().includes(search) ||
      (cil.project?.name || '').toLowerCase().includes(search) ||
      (readableStatus(cil.status) || '').toLowerCase().includes(search);

    const matchesStatus = selectedStatus
      ? cil.status === selectedStatus
      : true;

    return matchesText && matchesStatus;
  });

  // --------------------- CSV Export ---------------------
  const csvHeaders = [
    { label: 'CIL No.', key: 'id' },
    { label: 'Developer Name', key: 'developerName' },
    { label: 'Broker Name', key: 'broker.name' },
    { label: 'Broker Number', key: 'broker.mobile' },
    { label: 'Client Name', key: 'clientName' },
    { label: 'Client Number', key: 'clientNumber' },
    { label: 'Project', key: 'project.name' },
    { label: 'Status', key: 'status' },
    { label: 'Created At', key: 'created_at' },
    { label: 'Updated At', key: 'updated_at' }
  ];

  // --------------------- Custom Styles ---------------------
  const customStyles = {
    rows: {
      style: {
        fontSize: '15px',
      },
    },
  };

  return (
    <div className="card basic-data-table px-3 py-3">
      <div className="card-body">

        {/* Filters and CSV Export */}
        <div className="d-flex justify-content-start align-items-center mb-3 flex-wrap gap-2">
          {/* Text Filter */}
          <input
            type="text"
            placeholder="Search by any field..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="form-control"
            style={{ maxWidth: '300px' }}
          />

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="form-control"
            style={{ maxWidth: '200px' }}
          >
            <option value="">All Statuses</option>
            {distinctStatuses.map((status) => (
              <option key={status} value={status}>
                {readableStatus(status)}
              </option>
            ))}
          </select>

          {/* CSV Export */}
          <CSVLink
            data={cils}
            headers={csvHeaders}
            filename="cils-data-all.csv"
            className="btn btn-secondary"
          >
            Export All CILs CSV
          </CSVLink>
        </div>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          responsive
          striped
          customStyles={customStyles}
          noDataComponent="No results"
        />
      </div>

      {/* --------------------- Modals --------------------- */}

      {/* View CIL Modal */}
      {showViewModal && (
        <ViewCILModal
          show={showViewModal}
          handleClose={handleCloseViewModal}
          cil={currentCIL}
        />
      )}

      {/* Edit CIL Modal */}
      {showEditModal && (
        <EditCILModal
          show={showEditModal}
          handleClose={handleCloseEditModal}
          cil={editedCIL}
          handleSave={handleSaveEdit}
        />
      )}

      {/* Approve Pending Modal */}
      <Modal show={showApprovePendingModal} onHide={handleCloseApprovePendingModal}>
        <Modal.Header closeButton>
          <Modal.Title>Approve CIL – Add Expiration Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label htmlFor="expirationDate" className="form-label">
              Expiration Date
            </label>
            <input
              type="date"
              id="expirationDate"
              className="form-control"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary" onClick={handleApprovePendingSubmit} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Approve'}
          </button>
          <button className="btn btn-secondary" onClick={handleCloseApprovePendingModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      {/* Approve Pending Contract Modal */}
      <Modal
        show={showApproveContractPendingModal}
        onHide={handleCloseApproveContractPendingModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Approve Contract Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>

            <div className="mb-3">
              <strong>You are approving contract, please check it carefully first.{' '}</strong>
              <a
                href={selectedCIL?.document}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#487fff', textDecoration: 'underline', cursor: 'pointer' }}
              >
                View Document
              </a>
            </div>
          </p>
          <div className="mb-3">
            <label htmlFor="dealValue" className="form-label">
              Total Deal Value
            </label>
            <input
              type="number"
              id="dealValue"
              className="form-control"
              placeholder="Enter total deal value"
              value={dealValue}
              onChange={(e) => setDealValue(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="actualAmount" className="form-label">
              Actual Commission Amount
            </label>
            <input
              type="number"
              id="actualAmount"
              className="form-control"
              placeholder="Enter actual amount"
              value={actualAmount}
              onChange={(e) => setActualAmount(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-primary"
            onClick={handleApproveContractPendingSubmit}
            disabled={loading}
          >
            {loading ? <Spinner animation="border" size="sm" /> : 'Approve'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleCloseApproveContractPendingModal}
          >
            Cancel
          </button>
        </Modal.Footer>
      </Modal>


      {/* Approve Contract Approved Modal */}
      <Modal
        show={showApproveContractApproveModal}
        onHide={handleCloseApproveContractApproveModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Finalizing Deal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to finalize this deal? This will send the money to
            the Broker's available wallet.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary" onClick={handleApproveContractApproveSubmit} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Yes, Approve'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleCloseApproveContractApproveModal}
          >
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      {/* Reject Confirmation Modal with Reason */}
      <Modal show={showRejectModal} onHide={handleCloseRejectModal}>
        <Modal.Header closeButton>
          <Modal.Title>Reject CIL</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to reject this CIL?</p>
          <div className="mb-3">
            <label htmlFor="rejectReason" className="form-label">
              Reason for Rejection
            </label>
            <textarea
              id="rejectReason"
              className="form-control"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              required
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-danger" onClick={handleRejectConfirm} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Yes, Reject'}
          </button>
          <button className="btn btn-secondary" onClick={handleCloseRejectModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CILsTable;
